import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/api';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const FinancialsPage = () => {
  const [analytics, setAnalytics] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAnalytics();
    fetchHistory();
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/super-admin/financial-analytics`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setAnalytics(response.data);
    } catch (err) {
      console.error('Failed to fetch financial analytics:', err);
      setError(err.response?.data?.detail || 'Failed to load financial analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/super-admin/financial-history`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setHistory(response.data);
    } catch (err) {
      console.error('Failed to fetch financial history:', err);
    }
  };


  const formatCurrency = (amount) => {
    if (!analytics) return '0.00';
    const curr = analytics.currency || 'GBP';
    const symbol = curr === 'GBP' ? '£' : curr === 'USD' ? '$' : '€';
    return `${symbol}${parseFloat(amount || 0).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <span className="ml-3 text-gray-600">Loading financial analytics from PayPal...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl">
          <h1 className="text-3xl font-bold">Business Analytics</h1>
          <p className="text-purple-200 text-sm">Financial metrics and subscription analytics</p>
        </div>
        <div className="bg-red-50 border border-red-300 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={fetchAnalytics}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const MetricCard = ({ title, value, icon, bgColor, textColor, subtitle }) => (
    <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-200 border border-gray-200">
      <div className="p-6">
        <div className="flex items-center justify-between mb-3">
          <div className={`p-3 rounded-lg ${bgColor}`}>
            {icon}
          </div>
          <div className={`text-3xl font-bold ${textColor}`}>
            {value}
          </div>
        </div>
        <h3 className="text-gray-700 font-semibold text-base">{title}</h3>
        {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-purple-500 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-3xl font-bold">Revenue & Payment Metrics</h1>
              <p className="text-purple-200 text-sm">Revenue tracking, payment analytics, and transaction monitoring</p>
            </div>
          </div>
          <button
            onClick={() => {
              fetchAnalytics();
              fetchHistory();
            }}
            className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-white font-medium transition-all flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Last Updated */}
      <div className="text-sm text-gray-600">
        Last updated: {formatDateTime(analytics.last_updated)}
      </div>

      {/* Section 1: Revenue Metrics */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
          <svg className="w-6 h-6 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Revenue Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <MetricCard
            title="Monthly Recurring Revenue"
            value={formatCurrency(analytics.mrr)}
            subtitle="Expected monthly revenue"
            icon={<svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>}
            bgColor="bg-green-50"
            textColor="text-green-800"
          />
          <MetricCard
            title="Total Revenue to Date"
            value={formatCurrency(analytics.total_revenue)}
            subtitle="All-time revenue"
            icon={<svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
            bgColor="bg-emerald-50"
            textColor="text-emerald-800"
          />
          <MetricCard
            title="Average Revenue Per User"
            value={formatCurrency(analytics.arpu)}
            subtitle="ARPU"
            icon={<svg className="w-8 h-8 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>}
            bgColor="bg-teal-50"
            textColor="text-teal-800"
          />
          <MetricCard
            title="This Month Payments"
            value={formatCurrency(analytics.payments_this_month_amount)}
            subtitle={`${analytics.payments_this_month_count} payment(s)`}
            icon={<svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" /></svg>}
            bgColor="bg-blue-50"
            textColor="text-blue-800"
          />
        </div>
      </div>

      {/* Revenue by Tier Breakdown */}
      {Object.keys(analytics.revenue_by_tier || {}).length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue by Tier</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(analytics.revenue_by_tier).map(([tier, revenue]) => (
              <div key={tier} className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-200">
                <span className="text-gray-700 font-medium">{tier}</span>
                <span className="text-purple-800 font-bold">{formatCurrency(revenue)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Charts Section - Revenue Charts */}
      {history && history.months && (
        <div className="space-y-8">
          {/* Chart 1: MRR Trend Line Chart */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Recurring Revenue Trend</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={history.months}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Line type="monotone" dataKey="mrr" stroke="#10b981" strokeWidth={2} name="MRR" />
              </LineChart>
            </ResponsiveContainer>
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-gray-700">
                <strong>What this shows:</strong> Your expected monthly revenue from all active paid subscriptions over the last 12 months.
              </p>
              <p className="text-sm text-gray-700 mt-2">
                <strong>How to interpret:</strong> An upward trend indicates healthy business growth. Look for consistent month-over-month increases. Flat or declining trends may signal a need to focus on customer acquisition or retention strategies.
              </p>
            </div>
          </div>

          {/* Chart 2: Revenue by Tier (Pie Chart) */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Distribution by Tier</h3>
            {analytics && Object.keys(analytics.revenue_by_tier || {}).length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={Object.entries(analytics.revenue_by_tier).map(([tier, revenue]) => ({
                      name: tier,
                      value: revenue
                    }))}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {Object.keys(analytics.revenue_by_tier).map((entry, index) => {
                      const colors = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b'];
                      return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />;
                    })}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-gray-500">
                No revenue data available. Create PayPal subscriptions to see data.
              </div>
            )}
            <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <p className="text-sm text-gray-700">
                <strong>What this shows:</strong> The percentage of total revenue contributed by each subscription tier.
              </p>
              <p className="text-sm text-gray-700 mt-2">
                <strong>How to interpret:</strong> If one tier dominates (e.g., 70%+), consider whether you're pricing other tiers competitively. A balanced distribution suggests good market segmentation. Use this to guide marketing focus and feature development priorities.
              </p>
            </div>
          </div>

          {/* Chart 3: Revenue Stacked Area Chart */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue by Tier Over Time</h3>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={history.months}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Area type="monotone" dataKey="starter_revenue" stackId="1" stroke="#8b5cf6" fill="#8b5cf6" name="Starter" />
                <Area type="monotone" dataKey="business_revenue" stackId="1" stroke="#3b82f6" fill="#3b82f6" name="Business" />
                <Area type="monotone" dataKey="pro_revenue" stackId="1" stroke="#10b981" fill="#10b981" name="Pro" />
              </AreaChart>
            </ResponsiveContainer>
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-gray-700">
                <strong>What this shows:</strong> How revenue contribution from each subscription tier has evolved over time.
              </p>
              <p className="text-sm text-gray-700 mt-2">
                <strong>How to interpret:</strong> Watch for tier mix changes - if Pro revenue is growing faster than Starter, you're successfully upselling. If Starter dominates and isn't shrinking, you may need stronger upgrade incentives or more Pro features.
              </p>
            </div>
          </div>

          {/* Chart 4: Total Revenue vs MRR Comparison */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Total Revenue vs MRR Comparison</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={history.months}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis yAxisId="left" label={{ value: 'MRR (£)', angle: -90, position: 'insideLeft' }} />
                <YAxis yAxisId="right" orientation="right" label={{ value: 'Total Revenue (£)', angle: 90, position: 'insideRight' }} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Line yAxisId="left" type="monotone" dataKey="mrr" stroke="#10b981" strokeWidth={2} name="MRR" />
                <Line yAxisId="right" type="monotone" dataKey="cumulative_revenue" stroke="#3b82f6" strokeWidth={2} name="Total Revenue" />
              </LineChart>
            </ResponsiveContainer>
            <div className="mt-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
              <p className="text-sm text-gray-700">
                <strong>What this shows:</strong> A dual-axis chart comparing cumulative total revenue (all-time) against monthly recurring revenue over time.
              </p>
              <p className="text-sm text-gray-700 mt-2">
                <strong>How to interpret:</strong> The gap between total revenue and MRR shows your one-time revenue vs recurring revenue mix. A widening gap means good recurring revenue growth. If MRR is flat but total revenue grows, you're relying too much on one-time payments rather than subscriptions - focus on recurring revenue growth.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Payment Metrics */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
          <svg className="w-6 h-6 mr-2 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
          </svg>
          Payment Metrics
        </h2>

        <div className="space-y-8">
          {/* Chart 9: Payment Success Rate (Donut Chart) */}
          {analytics && analytics.payment_success_rate ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Success Rate</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Successful', value: analytics.payment_success_rate.successful },
                      { name: 'Failed', value: analytics.payment_success_rate.failed },
                      { name: 'Pending', value: analytics.payment_success_rate.pending }
                    ].filter(item => item.value > 0)}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    <Cell fill="#10b981" />
                    <Cell fill="#ef4444" />
                    <Cell fill="#fbbf24" />
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>What this shows:</strong> The percentage breakdown of payment statuses - successful (green), failed (red), and pending (yellow) - across all payment attempts.
                </p>
                <p className="text-sm text-gray-700 mt-2">
                  <strong>How to interpret:</strong> Aim for 95%+ success rate. Failed payments above 5% indicate billing issues - check for expired cards, insufficient funds, or payment gateway problems. High failure rates hurt revenue and require customer follow-up. Set up automatic retry logic and dunning emails to recover failed payments.
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Success Rate</h3>
              <div className="h-[300px] flex items-center justify-center text-gray-500">
                No payment data available. Process payments to see success rate.
              </div>
              <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>What this shows:</strong> The percentage breakdown of payment statuses - successful (green), failed (red), and pending (yellow) - across all payment attempts.
                </p>
                <p className="text-sm text-gray-700 mt-2">
                  <strong>How to interpret:</strong> Aim for 95%+ success rate. Failed payments above 5% indicate billing issues - check for expired cards, insufficient funds, or payment gateway problems. High failure rates hurt revenue and require customer follow-up. Set up automatic retry logic and dunning emails to recover failed payments.
                </p>
              </div>
            </div>
          )}

          {/* Chart 10: Monthly Payment Volume (Bar Chart) */}
          {history && history.months ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Payment Volume</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={history.months}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="payment_count" fill="#14b8a6" name="Number of Payments" />
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-4 p-4 bg-teal-50 border border-teal-200 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>What this shows:</strong> The total number of payment transactions processed each month over the last 12 months.
                </p>
                <p className="text-sm text-gray-700 mt-2">
                  <strong>How to interpret:</strong> Increasing payment volume indicates business growth. Seasonal patterns? Plan for high/low periods. Sudden drops may signal technical issues or customer dissatisfaction. Use this to forecast cash flow, plan for payment processing capacity, and identify seasonal trends for inventory or staffing.
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Payment Volume</h3>
              <div className="h-[300px] flex items-center justify-center text-gray-500">
                No payment history available. Process payments to see volume trends.
              </div>
              <div className="mt-4 p-4 bg-teal-50 border border-teal-200 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>What this shows:</strong> The total number of payment transactions processed each month over the last 12 months.
                </p>
                <p className="text-sm text-gray-700 mt-2">
                  <strong>How to interpret:</strong> Increasing payment volume indicates business growth. Seasonal patterns? Plan for high/low periods. Sudden drops may signal technical issues or customer dissatisfaction. Use this to forecast cash flow, plan for payment processing capacity, and identify seasonal trends for inventory or staffing.
                </p>
              </div>
            </div>
          )}

          {/* Chart 11: Payment Amount Distribution (Histogram) */}
          {analytics && analytics.payment_amount_distribution ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Amount Distribution</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart 
                  data={Object.entries(analytics.payment_amount_distribution).map(([range, count]) => ({
                    range,
                    count
                  }))}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="range" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" fill="#06b6d4" name="Transactions" />
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-4 p-4 bg-cyan-50 border border-cyan-200 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>What this shows:</strong> How payment amounts are distributed across different price ranges (£0-50, £51-100, £101-200, etc.).
                </p>
                <p className="text-sm text-gray-700 mt-2">
                  <strong>How to interpret:</strong> Most payments clustered around your plan prices indicates healthy subscription billing. Wide spread suggests mixed pricing or one-time charges. If most payments are in low ranges, consider if you're pricing too low or need premium tiers. High-value outliers? Investigate for potential enterprise opportunities or annual billing adoption.
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Amount Distribution</h3>
              <div className="h-[300px] flex items-center justify-center text-gray-500">
                No payment data available. Process payments to see amount distribution.
              </div>
              <div className="mt-4 p-4 bg-cyan-50 border border-cyan-200 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>What this shows:</strong> How payment amounts are distributed across different price ranges (£0-50, £51-100, £101-200, etc.).
                </p>
                <p className="text-sm text-gray-700 mt-2">
                  <strong>How to interpret:</strong> Most payments clustered around your plan prices indicates healthy subscription billing. Wide spread suggests mixed pricing or one-time charges. If most payments are in low ranges, consider if you're pricing too low or need premium tiers. High-value outliers? Investigate for potential enterprise opportunities or annual billing adoption.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Section 4: Recent Transactions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-xl font-semibold text-gray-900">Recent Transactions</h2>
          <p className="text-sm text-gray-600">Last 20 payments</p>
        </div>
        <div className="overflow-x-auto">
          {analytics.recent_transactions.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No transactions found
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase">Organisation</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {analytics.recent_transactions.map((txn, index) => (
                  <tr key={txn.id || index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">{formatDate(txn.date)}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{txn.organisation}</td>
                    <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                      {txn.currency} {txn.amount.toFixed(2)}
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                        {txn.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Section 5: Failed Payments */}
      {analytics.failed_payments.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-red-200">
          <div className="px-6 py-4 border-b border-red-100 bg-red-50">
            <h2 className="text-xl font-semibold text-red-900 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              Failed Payments
            </h2>
            <p className="text-sm text-red-700">Requires attention</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-red-50">
                  <th className="px-6 py-3 text-left text-xs font-medium text-red-900 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-red-900 uppercase">Organisation</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-red-900 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-red-900 uppercase">Reason</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {analytics.failed_payments.map((txn, index) => (
                  <tr key={txn.id || index} className="hover:bg-red-50">
                    <td className="px-6 py-4 text-sm text-gray-900">{formatDate(txn.date)}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{txn.organisation}</td>
                    <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                      {txn.currency} {txn.amount.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 text-sm text-red-700">{txn.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Section 6: Upcoming Renewals */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-xl font-semibold text-gray-900">Upcoming Renewals</h2>
          <p className="text-sm text-gray-600">Next 30 days</p>
        </div>
        <div className="overflow-x-auto">
          {analytics.upcoming_renewals.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No upcoming renewals in the next 30 days
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase">Organisation</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase">Tier</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {analytics.upcoming_renewals.map((renewal, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">{formatDate(renewal.date)}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{renewal.organisation}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{renewal.tier}</td>
                    <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                      {renewal.currency} {renewal.amount.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default FinancialsPage;
